import { generate_pylon_properties } from "./OLMapUtils";

/**
 * The DATA_HEADERS function returns an object with the following properties:
 * number, longitude, latitude, elevation, distance and angle.
 * The values of these properties are translations of the keys in English.
 *
 * @param t The translation object
 * @return An object with keys that are the translated values
 */
export function DATA_HEADERS(t) {
  return {
    number: t("common.Number"),
    longitude: t("common.Longitude"),
    latitude: t("common.Latitude"),
    elevation: t("common.Elevation"),
    distance: t("PylonReportDialog.DistanceToNext"),
    angle: t("PylonReportDialog.Angle")
  };
}

/**
 * The generateCsvData function takes in a pathId and pathsData object,
 * and returns an array of arrays. The first element is the header row,
 * which contains the names of each column. The remaining elements are rows
 * containing data for each pylon on that path.
 *
 * @param t The translation object
 * @param pathId Identify the path that is being exported
 * @param pathsData Get the pylon properties for each path
 * @return The CSV object data
 */
export function generateCsvData(t, pathId, pathsData) {
  const csvData = [DATA_HEADERS(t)];
  const propList = generate_pylon_properties(pathId, pathsData);

  return csvData.concat(propList);
}

/**
 * It is a common function to create a temporary url to download a file.
 * The downloadUrlLink function takes a URL, an extension, and a filename as arguments.
 * It creates an anchor element with the href set to the url argument and download attribute set to `${filename}.${extension}`.
 * The function then clicks on that anchor element which triggers the browser's default behavior of downloading files.
 * Finally, it removes that anchor element from the DOM.

 *
 * @param url Specify the location of the file to be downloaded
 * @param extension Determine the file type of the downloaded image
 * @param filename Set the name of the downloaded file
 *
 * @return A link that downloads the file
 */
export function downloadUrlLink(url, extension, filename = "chart") {
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}.${extension}`;
  a.click();
  a.remove();
}

// Characters that make a spreadsheet read the cell as a formula when they
// are the first one, and the operators that betray an actual formula inside
// a cell that merely starts like a negative number.
const CSV_FORMULA_START = /^[=+\-@\t\r]/;
const CSV_FORMULA_CHARS = /[=+|!]/;

/**
 * The isPlainNumericCell function tells whether a cell is just a number,
 * possibly negative and possibly carrying a unit, and therefore safe to write
 * as it is even though it starts with a character a spreadsheet could read as
 * the beginning of a formula.
 *
 * A cell qualifies when it starts with an optional minus followed by a digit
 * and holds no further formula operator. That keeps a formatted measurement
 * such as "-1,234.56 km" untouched while still catching a payload such as
 * "-2+3+cmd|' /C calc'!A0".
 *
 * @param {string} str The already stringified cell value
 *
 * @return {boolean} True when the cell can be written without a guard
 */
function isPlainNumericCell(str) {
  return /^-?\d/.test(str) && !CSV_FORMULA_CHARS.test(str.slice(1));
}

/**
 * The escapeCsvCell function turns a single value into a CSV-safe field.
 * Null and undefined become an empty field. When the value contains a
 * comma, a double quote or a line break, it is wrapped in double quotes
 * and its own quotes are doubled, as required by RFC 4180.
 *
 * Escaping matters for locale-formatted numbers: without it, a value such
 * as "1,234.5" would be read by the spreadsheet as two separate columns.
 *
 * Cells that start with "=", "+", "-", "@", a tab or a carriage return are
 * also prefixed with a single quote, because Excel and Sheets evaluate them
 * as formulas. Quoting alone does not help, they unquote the field first and
 * evaluate afterwards, so a path named '=HYPERLINK("http://x","Open")' would
 * become a live formula for whoever opens the file. The prefix is the
 * convention both applications hide on display, at the cost of a literal
 * quote for a plain parser such as pandas. Plain numbers are left alone.
 *
 * @param {*} value The value to write into the CSV field
 *
 * @return {string} The escaped, ready-to-concatenate CSV field
 */
export function escapeCsvCell(value) {
  let str = String(value ?? "");

  if (CSV_FORMULA_START.test(str) && !isPlainNumericCell(str)) {
    str = `'${str}`;
  }

  return /[",\r\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
}

// Byte order mark written in front of the CSV text. Excel on Windows assumes
// the system code page for a file without it, which turns accented names and
// symbols such as the euro sign into mojibake. Kept as an escape rather than a
// literal so it stays visible in the source.
const UTF8_BOM = "\uFEFF";

/**
 * The downloadCsvRows function serializes a table given as an array of rows
 * (each row being an array of cells, the first one usually the header) into a
 * CSV text, and triggers its download in the browser.
 *
 * Every cell is escaped with `escapeCsvCell`, rows are joined with CRLF as
 * expected by spreadsheet applications, the text is prefixed with a UTF-8 byte
 * order mark so Excel reads it as Unicode, and the result is handed to
 * `downloadUrlLink` through a temporary object URL that is revoked right after.
 *
 * @param {Array<Array>} rows The table to export, one array per row
 * @param {string} filename Name of the downloaded file, without extension
 *
 * @return {void} Nothing, the file download is started as a side effect
 */
export function downloadCsvRows(rows, filename = "chart") {
  if (!rows || rows.length === 0) return;

  const csv = rows.map(row => row.map(escapeCsvCell).join(",")).join("\r\n");

  const blob = new Blob([UTF8_BOM + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  downloadUrlLink(url, "csv", filename);
  URL.revokeObjectURL(url);
}

/**
 * The getPrettyFileSize function takes a number of bytes and returns a string with the file size in a human-readable format.
 *
 * @param {number} bytes - The number of bytes to convert to a human-readable format
 * @returns {string} A string with the file size in a human-readable format
 */
export function getPrettyFileSize(bytes) {
  const units = ["B", "KB", "MB", "GB"];
  const k = 1024;

  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const size = (bytes / Math.pow(k, i)).toFixed(0);

  return `${i === 0 ? bytes : size} ${units[i]}`;
}

/**
 * Sanitize a filename by removing or replacing invalid characters. The
 * characters a file system rejects are dropped, every run of whitespace
 * becomes a single underscore, and the result is capped at 255 characters.
 *
 * The input is coerced, so null and undefined give an empty string instead of
 * throwing. Note the result can legitimately be empty, for instance when the
 * input was only made of illegal characters, so callers that build a name out
 * of it should keep their own fallback: `sanitizeFilename(name) || "chart"`.
 *
 * @param {*} filename The candidate name, without extension
 *
 * @returns {string} The sanitized name, possibly empty
 */
export function sanitizeFilename(filename) {
  return String(filename ?? "")
    .replace(/[<>:"/\\|?*\u0000-\u001F]/g, "") // eslint-disable-line no-control-regex
    .replace(/\s+/g, "_")
    .substring(0, 255);
}
